import { useState, useCallback, useEffect, RefObject } from 'react';

export interface ScrollbarState {
  thumbHeight: number;
  thumbTop: number;
  thumbWidth: number;
  thumbLeft: number;
}

export interface ScrollbarVisibility {
  hasVerticalOverflow: boolean;
  hasHorizontalOverflow: boolean;
  verticalScrollbarVisible: boolean;
  horizontalScrollbarVisible: boolean;
}

export interface ScrollbarHandlers {
  handleVerticalThumbMouseDown: (e: React.MouseEvent) => void;
  handleHorizontalThumbMouseDown: (e: React.MouseEvent) => void;
  handleVerticalTrackClick: (e: React.MouseEvent) => void;
  handleHorizontalTrackClick: (e: React.MouseEvent) => void;
  handleScrollbarMouseEnter: () => void;
  handleScrollbarMouseLeave: () => void;
  updateScrollbarPositions: () => void;
}

export interface UseCustomScrollbarReturn {
  verticalScrollState: { thumbHeight: number; thumbTop: number };
  horizontalScrollState: { thumbWidth: number; thumbLeft: number };
  isDraggingVertical: boolean;
  isDraggingHorizontal: boolean;
  visibility: ScrollbarVisibility;
  handlers: ScrollbarHandlers;
}

/**
 * Custom hook to manage scrollbar state and interactions for a scrollable element
 * @param scrollableRef - Reference to the scrollable element
 * @returns Object containing scrollbar state, visibility flags, and event handlers
 */
export const useCustomScrollbar = (
  scrollableRef: RefObject<HTMLElement>
): UseCustomScrollbarReturn => {
  // Scrollbar position state
  const [verticalScrollState, setVerticalScrollState] = useState({ 
    thumbHeight: 0, 
    thumbTop: 0 
  });
  const [horizontalScrollState, setHorizontalScrollState] = useState({ 
    thumbWidth: 0, 
    thumbLeft: 0 
  });

  // Drag state
  const [isDraggingVertical, setIsDraggingVertical] = useState(false);
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);

  // Overflow and visibility state
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [verticalScrollbarVisible, setVerticalScrollbarVisible] = useState(false);
  const [horizontalScrollbarVisible, setHorizontalScrollbarVisible] = useState(false);

  /**
   * Update scrollbar positions based on current scroll state
   */
  const updateScrollbarPositions = useCallback(() => {
    if (!scrollableRef.current) return;

    const element = scrollableRef.current;
    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = element;

    // Vertical scrollbar calculations
    const verticalOverflow = scrollHeight > clientHeight;
    setHasVerticalOverflow(verticalOverflow);
    
    if (verticalOverflow) {
      const trackHeight = clientHeight;
      const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      const maxThumbTop = trackHeight - thumbHeight;
      const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
      const thumbTop = scrollPercentage * maxThumbTop;

      setVerticalScrollState({ thumbHeight, thumbTop });
    }

    // Horizontal scrollbar calculations
    const horizontalOverflow = scrollWidth > clientWidth;
    setHasHorizontalOverflow(horizontalOverflow);
    
    if (horizontalOverflow) {
      const trackWidth = clientWidth;
      const thumbWidth = Math.max(30, (clientWidth / scrollWidth) * trackWidth);
      const maxThumbLeft = trackWidth - thumbWidth;
      const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
      const thumbLeft = scrollPercentage * maxThumbLeft;

      setHorizontalScrollState({ thumbWidth, thumbLeft });
    }
  }, [scrollableRef]);

  /**
   * Handle vertical scrollbar thumb drag
   */
  const handleVerticalThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!scrollableRef.current) return;

    setIsDraggingVertical(true);
    
    const element = scrollableRef.current;
    const startY = e.clientY;
    const startScrollTop = element.scrollTop;
    const trackHeight = element.clientHeight;
    const maxScroll = element.scrollHeight - element.clientHeight;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const scrollDelta = (deltaY / trackHeight) * maxScroll;
      element.scrollTop = Math.max(0, Math.min(maxScroll, startScrollTop + scrollDelta));
    };

    const handleMouseUp = () => {
      setIsDraggingVertical(false);
      setVerticalScrollbarVisible(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [scrollableRef]);

  /**
   * Handle horizontal scrollbar thumb drag
   */
  const handleHorizontalThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!scrollableRef.current) return;

    setIsDraggingHorizontal(true);
    
    const element = scrollableRef.current;
    const startX = e.clientX;
    const startScrollLeft = element.scrollLeft;
    const trackWidth = element.clientWidth;
    const maxScroll = element.scrollWidth - element.clientWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const scrollDelta = (deltaX / trackWidth) * maxScroll;
      element.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + scrollDelta));
    };

    const handleMouseUp = () => {
      setIsDraggingHorizontal(false);
      setHorizontalScrollbarVisible(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [scrollableRef]);

  /**
   * Handle vertical scrollbar track click (jump to position)
   */
  const handleVerticalTrackClick = useCallback((e: React.MouseEvent) => {
    if (!scrollableRef.current) return;
    
    const element = scrollableRef.current;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const trackHeight = rect.height;
    const targetPercentage = clickY / trackHeight;
    const maxScroll = element.scrollHeight - element.clientHeight;
    element.scrollTop = targetPercentage * maxScroll;
  }, [scrollableRef]);

  /**
   * Handle horizontal scrollbar track click (jump to position)
   */
  const handleHorizontalTrackClick = useCallback((e: React.MouseEvent) => {
    if (!scrollableRef.current) return;
    
    const element = scrollableRef.current;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const targetPercentage = clickX / trackWidth;
    const maxScroll = element.scrollWidth - element.clientWidth;
    element.scrollLeft = targetPercentage * maxScroll;
  }, [scrollableRef]);

  /**
   * Show scrollbars on mouse enter (if there's overflow)
   */
  const handleScrollbarMouseEnter = useCallback(() => {
    if (hasVerticalOverflow) {
      setVerticalScrollbarVisible(true);
    }
    if (hasHorizontalOverflow) {
      setHorizontalScrollbarVisible(true);
    }
  }, [hasVerticalOverflow, hasHorizontalOverflow]);

  /**
   * Hide scrollbars on mouse leave (unless dragging)
   */
  const handleScrollbarMouseLeave = useCallback(() => {
    if (!isDraggingVertical) {
      setVerticalScrollbarVisible(false);
    }
    if (!isDraggingHorizontal) {
      setHorizontalScrollbarVisible(false);
    }
  }, [isDraggingVertical, isDraggingHorizontal]);

  // Update scrollbar positions on scroll
  useEffect(() => {
    updateScrollbarPositions();
  }, [updateScrollbarPositions]);

  return {
    verticalScrollState,
    horizontalScrollState,
    isDraggingVertical,
    isDraggingHorizontal,
    visibility: {
      hasVerticalOverflow,
      hasHorizontalOverflow,
      verticalScrollbarVisible,
      horizontalScrollbarVisible,
    },
    handlers: {
      handleVerticalThumbMouseDown,
      handleHorizontalThumbMouseDown,
      handleVerticalTrackClick,
      handleHorizontalTrackClick,
      handleScrollbarMouseEnter,
      handleScrollbarMouseLeave,
      updateScrollbarPositions,
    },
  };
};


